/**
 * Credentials Manager
 * Credentials kezelő felület (hasonló az n8n-hez)
 */

import React, { useState } from 'react';
import { useCredentials } from '../../hooks/useCredentials';
import { Credential, CredentialType, CREDENTIAL_TYPES } from '../../types/credentials';
import CredentialEditor from './CredentialEditor';

const CredentialsManager: React.FC = () => {
  const { credentials, loading, error, remove } = useCredentials();
  const [selectedCredential, setSelectedCredential] = useState<Credential | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [selectedType, setSelectedType] = useState<CredentialType | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // Kategóriák
  const categories = [
    { value: 'all', label: 'Összes', icon: '📁' },
    { value: 'communication', label: 'Kommunikáció', icon: '📧' },
    { value: 'ai', label: 'AI', icon: '🤖' },
    { value: 'database', label: 'Adatbázis', icon: '🗄️' },
    { value: 'integration', label: 'Integráció', icon: '🔌' },
    { value: 'payment', label: 'Fizetési szolgáltatók', icon: '💳' },
    { value: 'other', label: 'Egyéb', icon: '⚙️' }
  ];

  // Szűrt credentials
  const filteredCredentials = credentials.filter(cred => {
    if (filterCategory === 'all') return true;
    const metadata = CREDENTIAL_TYPES[cred.type];
    return metadata?.category === filterCategory;
  });

  // Credential törlése
  const handleDelete = async (credentialId: string) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a credentialt?')) {
      return;
    }

    try {
      await remove(credentialId);
    } catch (error) {
      console.error('Error deleting credential:', error);
      alert('Hiba történt a törlés során');
    }
  };

  // Új credential létrehozása
  const handleCreateNew = (type: CredentialType) => {
    setSelectedType(type);
    setIsCreating(true);
  };

  // Credential másolása
  const handleCopy = (credential: Credential) => {
    // Új credential létrehozása a másolat adataival
    // organizationId-t töröljük, hogy új credential-ként lehessen választani a szervezetet
    const copiedCredential: Credential = {
      ...credential,
      id: '', // Új ID-t kap majd
      name: `${credential.name} (másolat)`,
      organizationId: '', // Üres, hogy választható legyen a szervezet
      usageCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setSelectedCredential(copiedCredential);
    setIsCopying(true);
  };

  // Szerkesztés bezárása
  const handleCloseEditor = () => {
    setSelectedCredential(null);
    setIsCreating(false);
    setIsCopying(false);
    setSelectedType(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Betöltés...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <p className="text-red-700 dark:text-red-300">Hiba: {error}</p>
      </div>
    );
  }

  // Editor megjelenítése
  if (isCreating && selectedType) {
    return (
      <CredentialEditor
        type={selectedType}
        onClose={handleCloseEditor}
      />
    );
  }

  if (selectedCredential) {
    return (
      <CredentialEditor
        credential={selectedCredential}
        onClose={handleCloseEditor}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            🔐 Credentials
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            API kulcsok, jelszavak és kapcsolatok kezelése
          </p>
        </div>
      </div>

      {/* Kategória szűrő */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(cat => (
          <button
            key={cat.value}
            onClick={() => setFilterCategory(cat.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              filterCategory === cat.value
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Új credential gombok */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Új credential hozzáadása
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {Object.values(CREDENTIAL_TYPES)
            .filter(type => filterCategory === 'all' || type.category === filterCategory)
            .map(type => (
              <button
                key={type.type}
                onClick={() => handleCreateNew(type.type)}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                {type.logo ? (
                  <img 
                    src={type.logo} 
                    alt={type.label} 
                    className="h-8 w-auto object-contain"
                  />
                ) : (
                  <span className="text-3xl">{type.icon}</span>
                )}
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {type.label}
                </span>
              </button>
            ))}
        </div>
      </div>

      {/* Meglévő credentials lista */}
      {filteredCredentials.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Meglévő credentials ({filteredCredentials.length})
          </h3>
          <div className="space-y-2">
            {filteredCredentials.map(credential => {
              const metadata = CREDENTIAL_TYPES[credential.type];
              return (
                <div
                  key={credential.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {metadata?.logo ? (
                        <img 
                          src={metadata.logo} 
                          alt={metadata.label} 
                          className="h-6 w-auto object-contain mt-1"
                        />
                      ) : (
                        <div className="text-2xl">{metadata?.icon || '🔑'}</div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                            {credential.name}
                          </h4>
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-xs rounded">
                            {metadata?.label || credential.type}
                          </span>
                        </div>
                        {credential.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {credential.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            Használva: {credential.usageCount || 0} flow-ban
                          </span>
                          <span>
                            Létrehozva: {new Date(credential.createdAt).toLocaleDateString('hu-HU')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedCredential(credential)}
                        className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 transition-colors"
                      >
                        Szerkesztés
                      </button>
                      <button
                        onClick={() => handleCopy(credential)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                        title="Másolás másik szervezetbe"
                      >
                        📋 Másolás
                      </button>
                      <button
                        onClick={() => handleDelete(credential.id)}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                      >
                        Törlés
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {filteredCredentials.length === 0 && credentials.length > 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Nincs credential ebben a kategóriában
        </div>
      )}

      {credentials.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">🔐</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Még nincs credential
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Hozz létre egy új credentialt a fenti gombok valamelyikével
          </p>
        </div>
      )}
    </div>
  );
};

export default CredentialsManager;
